import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import { NavigateFunction } from 'react-router-dom';
import './ProfilePage.css';

// Import Redux actions and selectors
import GetMeActions from '../../../redux/GetMeRedux';
import UpdateProfilePicActions from '../../../redux/UpdateProfilePicRedux';
import GetChildrenActions from '../../../redux/GetChildrenRedux';
import CreateWithdrawalActions from '../../../redux/CreateWithdrawalRedux';
import { GetMeSelectors } from '../../../redux/GetMeRedux';
import { UpdateProfilePicSelectors } from '../../../redux/UpdateProfilePicRedux';
import { GetChildrenSelectors } from '../../../redux/GetChildrenRedux';
import { CreateWithdrawalsSelectors } from '../../../redux/CreateWithdrawalRedux';


// Import assets
import { DefaultAvatar } from '../../../assets/images';
import { ReactComponent as ArrowRightIcon } from '../../../assets/icons/arrow-right.svg';
import AlertBox from '../../../components/AlertBox';

interface ProfilePageProps extends WithTranslation {
  navigate: NavigateFunction;
  // UpdateProfilePic Redux props
  updateProfilePicFetching: boolean;
  updateProfilePicSuccess: boolean;
  updateProfilePicError: boolean;
  updateProfilePic: (id: string, image: string) => void;
  // GetChildren Redux props
  children: any[];
  getChildrenFetching: boolean;
  getChildrenSuccess: boolean;
  getChildrenError: boolean;
  getChildren: () => void;
  // GetMe Redux props
  userData: any;
  getMeFetching: boolean;
  getMeSuccess: boolean;
  getMeError: boolean;
  getMe: (accessToken: string) => void;
  // CreateWithdrawal Redux props
  createWithdrawalFetching: boolean;
  createWithdrawalSuccess: boolean;
  createWithdrawalError: boolean;
  createWithdrawalData: any;
  createWithdrawals: (data: { salesforce_contact_id: string }) => void;
  clearWithdrawalData: () => void;
  // CurrentSettings Redux props
  currentSettings: any;
}

interface ProfilePageState {
  loading: boolean;
  showImageUpload: boolean;
  selectedImage: string | null;
  profilePic: string | null;
  name: string;
  dob: Date | null;
  ageYear: number;
  ageMonth: number;
  ageMonths: number;
  programmes: string;
  downloads: any[];
  childData: any; // Added for child data from navigation state
  childId: string; // Added for child ID from navigation state
  withdrawalLoading: boolean; // Add this line
  updateProfilePopup: boolean;
  updateProfileType: string;
}

class ProfilePage extends Component<ProfilePageProps, ProfilePageState> {
  constructor(props: ProfilePageProps) {
    super(props);
    
    // Get child data directly from Redux state like the reference project
    const child = props.currentSettings?.child;
    
    if (child) {
      // Process child data like the reference project does
      const dob = new Date(child.dob?.date || child.dob);
      const programmes = child.programmes || [];
      let programmesName = "";
      
      if (programmes.length > 0) {
        programmes.forEach((item: any) => {
          programmesName = programmesName.concat(item.name, ", ");
        });
        programmesName = programmesName.slice(0, -2);
      }
      
      this.state = {
        loading: false,
        childData: child,
        childId: child.id,
        profilePic: child.profile_pic ? child.profile_pic.url : null,
        name: child.nickname || child.name,
        dob: dob,
        ageYear: this.calculateAgeYear(dob),
        ageMonth: this.calculateAgeMonth(dob),
        ageMonths: this.calculateAgeMonths(dob),
        programmes: programmesName,
        downloads: child.assessments || [],
        selectedImage: null,
        showImageUpload: false,
        withdrawalLoading: false, // Initialize here
        updateProfilePopup: false,
        updateProfileType: '',
      };
    } else {
      // No child data available yet, show loading state
      this.state = {
        loading: true,
        childData: null,
        childId: '',
        profilePic: null,
        name: '',
        dob: null,
        ageYear: 0,
        ageMonth: 0,
        ageMonths: 0,
        programmes: '',
        downloads: [],
        selectedImage: null,
        showImageUpload: false,
        withdrawalLoading: false, // Initialize here
        updateProfilePopup: false,
        updateProfileType: '',
      };
    }
  }

  componentDidMount() {
    console.log('ProfilePage: componentDidMount, currentSettings:', this.props.currentSettings);
    // Check if we need to load data
    if (!this.props.currentSettings?.child) {
      console.log('ProfilePage: No child data, loading user data');
      this.loadUserDataIfNeeded();
    } else {
      console.log('ProfilePage: Child data available:', this.props.currentSettings.child);
    }
  }

  componentDidUpdate(prevProps: ProfilePageProps) {
    // Debug: Log all withdrawal-related prop changes
    if (prevProps.createWithdrawalFetching !== this.props.createWithdrawalFetching) {
      console.log('ProfilePage: createWithdrawalFetching changed:', {
        prev: prevProps.createWithdrawalFetching,
        current: this.props.createWithdrawalFetching
      });
    }
    
    if (prevProps.createWithdrawalData !== this.props.createWithdrawalData) {
      console.log('ProfilePage: createWithdrawalData changed:', {
        prev: prevProps.createWithdrawalData,
        current: this.props.createWithdrawalData
      });
    }
    
    // Debug: Log current Redux state for withdrawal
    console.log('ProfilePage: Current withdrawal Redux state:', {
      createWithdrawalData: this.props.createWithdrawalData,
      createWithdrawalFetching: this.props.createWithdrawalFetching,
      createWithdrawalSuccess: this.props.createWithdrawalSuccess,
      createWithdrawalError: this.props.createWithdrawalError
    });

    // for child data changes
    if ( prevProps.children && this.props.children && JSON.stringify(prevProps.children) !== JSON.stringify(this.props.children) ){
      if (  this.props.children.length > 0 ){
        const child = this.props.children.find((obj: any) => obj.id === this.props.currentSettings?.child?.id);
        if (child) {
          this.updateProfileDataFromChild(child);
        }
      }
    }
    
    // Handle currentSettings changes (when child is set)
    if (prevProps.currentSettings?.child?.id !== this.props.currentSettings?.child?.id && this.props.currentSettings?.child) {
      console.log('Current child changed, updating profile data');
      this.updateProfileDataFromChild(this.props.currentSettings.child);
    }
    
    // Handle profile picture upload response
    if (prevProps.updateProfilePicFetching !== this.props.updateProfilePicFetching) {
      if (!this.props.updateProfilePicFetching) {
        if (this.props.updateProfilePicSuccess) {
          console.log('Profile picture upload successful');
          this.setState({ showImageUpload: false, selectedImage: null, updateProfilePopup: true,updateProfileType: 'SUCCESS' });
          this.props.getChildren();
          // Refresh profile data if needed
          this.loadUserDataIfNeeded();

        } else if (this.props.updateProfilePicError) {
          console.log('Profile picture upload failed');
          this.setState({ showImageUpload: false, selectedImage: null, updateProfilePopup: true,updateProfileType: 'ERROR' });
          // alert(this.props.t('profile-screen:alert.update-profile-pic-fail.message'));
        }
      }
    }

    // Handle withdrawal creation response
    if (prevProps.createWithdrawalData !== this.props.createWithdrawalData && this.props.createWithdrawalData) {
      console.log('Withdrawal data changed:', {
        prev: prevProps.createWithdrawalData,
        current: this.props.createWithdrawalData,
        success: this.props.createWithdrawalData?.success
      });
      
      if (this.props.createWithdrawalData?.success === false) {
        alert(this.props.createWithdrawalData?.message || this.props.t('profile-screen:alert.withdrawal-failed.message'));
      } else if (this.props.createWithdrawalData?.success === true) {
        // Navigate to withdrawal screen on success
        // The createWithdrawalData is the response data directly (dataWithdrawals from Redux)
        const withdrawalId = this.props.createWithdrawalData?.id || this.props.createWithdrawalData?.data?.id;
        console.log('ProfilePage: Withdrawal successful, navigating to withdrawal page');
        console.log('ProfilePage: Withdrawal ID from response:', withdrawalId);
        console.log('ProfilePage: Full withdrawal response:', this.props.createWithdrawalData);
        
        if (withdrawalId) {
          
          // Clear withdrawal data after successful navigation
          console.log('ProfilePage: Clearing withdrawal data after successful navigation');
          this.props.clearWithdrawalData();

          // Navigate to withdrawal page
          this.props.navigate('/withdrawal', { 
            state: { withdrawal_id: withdrawalId }
          });
        } else {
          console.error('ProfilePage: No withdrawal ID in response data');
          console.error('ProfilePage: Response structure:', this.props.createWithdrawalData);
          alert('Withdrawal created but no ID received. Please try again.');
        }
      }
    }

    // Reset loader when withdrawal API finishes
    if (
      prevProps.createWithdrawalFetching &&
      !this.props.createWithdrawalFetching
    ) {
      console.log('Withdrawal API finished, resetting loader');
      this.setState({ withdrawalLoading: false });
    }
  }

  loadUserDataIfNeeded = () => {
    // Only load data if absolutely necessary (like after profile picture update)
    const hasUserData = this.props.userData && Object.keys(this.props.userData).length > 0;
    const hasChildrenData = this.props.children && this.props.children.length > 0;
    
    // Only fetch if data is missing
    if (!hasUserData) {
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        this.props.getMe(accessToken);
      }
    }
    
    if (!hasChildrenData) {
      this.props.getChildren();
    }
  };

  updateProfileDataFromChild = (child: any) => {
    // Process child data and update state
    const dob = new Date(child.dob?.date || child.dob);
    const programmes = child.programmes || [];
    let programmesName = "";
    
    if (programmes.length > 0) {
      programmes.forEach((item: any) => {
        programmesName = programmesName.concat(item.name, ", ");
      });
      programmesName = programmesName.slice(0, -2);
    }
    
    this.setState({
      loading: false,
      childData: child,
      childId: child.id,
      profilePic: child.profile_pic ? child.profile_pic.url : null,
      name: child.nickname || child.name,
      dob: dob,
      ageYear: this.calculateAgeYear(dob),
      ageMonth: this.calculateAgeMonth(dob),
      ageMonths: this.calculateAgeMonths(dob),
      programmes: programmesName,
      downloads: child.assessments || [],
    });
  };

  calculateAgeYear = (dob: Date): number => {
    // ✅ IMPROVED: Better age calculation logic
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    
    // Adjust age if birthday hasn't occurred yet this year
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    
    return Math.max(0, age); // Ensure age is never negative
  };

  calculateAgeMonth = (dob: Date): number => {
    // ✅ IMPROVED: More accurate month calculation
    const today = new Date();
    let months = (today.getFullYear() - dob.getFullYear()) * 12 + (today.getMonth() - dob.getMonth());
    
    // Adjust if day hasn't occurred yet this month
    if (today.getDate() < dob.getDate()) {
      months--;
    }
    
    return Math.max(0, months % 12); // Return months within current year
  };

  calculateAgeMonths = (dob: Date): number => {
    // ✅ IMPROVED: Total months calculation
    const today = new Date();
    let months = (today.getFullYear() - dob.getFullYear()) * 12 + (today.getMonth() - dob.getMonth());
    
    // Adjust if day hasn't occurred yet this month
    if (today.getDate() < dob.getDate()) {
      months--;
    }
    
    return Math.max(0, months); // Ensure total months is never negative
  };

  // Navigation functions
  _onBackPress = () => {
    this.props.navigate('/settings');
  };

  _onNavigateToWithdrawal = () => {
    this.props.navigate('/withdrawal');
  };

  // Profile management functions - Simplified for web
  _onChangePicture = () => {
    // Direct file input for web - no ActionSheet needed
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    input.onchange = (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (file) {
        this._processImageFile(file);
      }
    };
    
    input.click();
  };

  _processImageFile = (file: File) => {
    // File validation
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      alert(this.props.t('profile-screen:alert.file-too-large.message'));
      return;
    }

    if (!file.type.startsWith('image/')) {
      alert(this.props.t('profile-screen:alert.invalid-file.message'));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      this.setState({ 
        selectedImage: result,
        showImageUpload: true 
      });
    };
    reader.readAsDataURL(file);
  };

  _onImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      this._processImageFile(file);
    }
  };

  _onUploadPicture = () => {
    const { selectedImage, childId } = this.state;
    const { children } = this.props;
    
    // Try to get childId from multiple sources
    let uploadChildId = childId;
    
    if (!uploadChildId && children && children.length > 0) {
      uploadChildId = children[0].id;
      console.log('Using fallback childId from children array:', uploadChildId);
    }
    
    if (!uploadChildId) {
      console.error('No child ID available from any source');
      console.log('State childId:', childId);
      console.log('Props children:', children);
      console.log('Props currentSettings:', this.props.currentSettings);
      alert(this.props.t('profile-screen:alert.no-child-info.message'));
      return;
    }
    
    if (selectedImage) {
      console.log('Starting profile picture upload...');
      console.log('Image data type:', typeof selectedImage);
      console.log('Image data preview:', selectedImage.substring(0, 100) + '...');
      console.log('Child ID for upload:', uploadChildId);
      
      // Call the API with (childId, base64String) like reference project
      console.log('Calling updateProfilePic with childId and base64 image:', uploadChildId, selectedImage.substring(0, 100) + '...');
      this.props.updateProfilePic(uploadChildId, selectedImage);
      
      // Don't close modal yet - wait for response
      console.log('Upload API call made, waiting for response...');
    }
  };

  _onCancelImageUpload = () => {
    this.setState({ 
      showImageUpload: false, 
      selectedImage: null 
    });
  };

  _onWithdrawals = () => {
    console.log('ProfilePage: _onWithdrawals called');
    console.log('ProfilePage: currentSettings:', this.props.currentSettings);
    console.log('ProfilePage: currentSettings.child:', this.props.currentSettings?.child);
    
    // ✅ IMPROVED: Better error handling and validation
    if (!this.props.currentSettings) {
      console.log('ProfilePage: No currentSettings available');
      alert(this.props.t('profile-screen:alert.settings-not-available.message') || 'Settings data not available. Please refresh the page and try again.');
      return;
    }
    
    if (!this.props.currentSettings.child) {
      console.log('ProfilePage: No child data in currentSettings');
      alert(this.props.t('profile-screen:alert.child-info-not-available.message') || 'Child information not available. Please refresh the page and try again.');
      return;
    }
    
    if (!this.props.currentSettings.child.salesforce_contact_id) {
      console.log('ProfilePage: No salesforce_contact_id in child data');
      alert(this.props.t('profile-screen:alert.contact-info-not-available.message') || 'Unable to create withdrawal request. Contact information not available.');
      return;
    }
    
    console.log('ProfilePage: All validations passed, creating withdrawal');
    this.setState({ withdrawalLoading: true }); // Set loader
    try {
      const withdrawalData = {
        salesforce_contact_id: this.props.currentSettings.child.salesforce_contact_id,
      };
      console.log('ProfilePage: Creating withdrawal with data:', withdrawalData);
      this.props.createWithdrawals(withdrawalData);
    } catch (error) {
      console.error('ProfilePage: Error in _onWithdrawals:', error);
      this.setState({ withdrawalLoading: false }); // Reset loader on error
      alert(this.props.t('profile-screen:alert.withdrawal-failed.message'));
    }
  };

  _onDownloadItemPress = (url: string) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  renderDownload = () => {
    const { downloads } = this.state;
    
    console.log('renderDownload called with downloads:', downloads);
    
    // Always render the download section, even if empty
    return (
      <div className="download-section">
        <h2 className="download-header">{this.props.t('profile-screen:section.title.download')}</h2>
        <div className="download-container">
          {downloads && downloads.length > 0 ? (
            <div className="download-list">
              {downloads.map((item: any) => (
                <div key={item.id} className="download-item">
                  <button 
                    className="download-item-button"
                    onClick={() => this._onDownloadItemPress(item.report_url)}
                  >
                    <span className="download-item-text">
                      {item.report_name}
                    </span>
                    <ArrowRightIcon className="download-item-icon" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-downloads">
              <p>No assessment reports available for download.</p>
              <p>Reports will appear here once they are generated.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  shouldShowLoading = () => {
    // Only show loading if we don't have cached data and are actually fetching
    const hasCachedData = this.state.profilePic || this.state.name;
    const isFetching = this.props.getMeFetching || this.props.getChildrenFetching;
    
    return !hasCachedData && isFetching;
  };

  render() {
    const { 
      loading,
      showImageUpload, 
      selectedImage, 
      profilePic,
      name,
      dob,
      ageYear,
      ageMonth,
      ageMonths,
      programmes
    } = this.state;

    // Get Redux props
    const { 
      updateProfilePicFetching,
      updateProfilePicSuccess,
      updateProfilePicError,
      createWithdrawalFetching
    } = this.props;

    // Check if we should show loading state
    const shouldShowLoading = this.shouldShowLoading();

    // Show loading state if no child data is available
    if (loading) {
      return (
        <div className="profile-page">
          <div className="profile-container">
            <div className="loading-container">
              <div className="loading-spinner">Loading...</div>
              <p>{this.props.t('profile-screen:loading.profile-data')}</p>
            </div>
          </div>
        </div>
      );
    }

    // Show error state if there's an error and no cached data
    if ((this.props.getMeError || this.props.getChildrenError) && !this.state.profilePic) {
      return (
        <div className="profile-page">
          <div className="profile-container">
            <div className="error-container">
              <h2>{this.props.t('profile-screen:error.loading-profile')}</h2>
              <p>{this.props.t('profile-screen:error.failed-to-load')}</p>
              <button onClick={this.loadUserDataIfNeeded} className="retry-button">
                {this.props.t('profile-screen:button.retry')}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="profile-page">
        <div className="profile-container">
          {/* Header */}
          <div className="page-header">
            <div className="page-header-inner">
              <div className="header-left">
                <button 
                  className="header-button back-button"
                  onClick={() => this.props.navigate(-1)}
                >
                  <svg width="16" height="11" viewBox="0 0 16 11" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.79646 0.204319C6.07989 0.482194 6.07989 0.943465 5.80201 1.2269L2.42862 4.61141H15.2831C15.6777 4.61141 16 4.93374 16 5.33388C16 5.73402 15.6777 6.05635 15.2831 6.05635H2.42862L5.80757 9.44086C6.08545 9.72429 6.07989 10.18 5.80201 10.4634C5.51858 10.7413 5.06843 10.7413 4.78499 10.4579L0.205627 5.84517C0.144495 5.77848 0.0944773 5.70623 0.0555749 5.61731C0.0166725 5.52839 0 5.43391 0 5.33944C0 5.15048 0.0722476 4.97264 0.205627 4.8337L4.78499 0.220992C5.05731 -0.0679974 5.51303 -0.073555 5.79646 0.204319Z" fill="black"></path></svg>
                </button>
                <h1 className="header-title">{this.props.t('profile-screen:title')}</h1>
              </div>
            </div>
          </div>

          {/* Loading Animation - Only show when actually needed */}
          {shouldShowLoading && (
            <div className="loading-animation">
              <div className="loading-spinner"></div>
              <p>{this.props.t('profile-screen:loading.profile-data')}</p>
            </div>
          )}

          <div className="page-content">
            {/* Profile Picture Section */}
            <div className="profile-picture-section">
              <div className="profile-picture-container">
                <div className="profile-picture-wrapper">
                  <img 
                    src={profilePic || DefaultAvatar} 
                    alt="Profile" 
                    className="profile-picture"
                  />
                </div>
                <div className="profile-picture-actions">
                  <button 
                    className="change-picture-btn"
                    onClick={this._onChangePicture}
                  >
                    {this.props.t('profile-screen:button.upload-profile-photo')}
                  </button>
                </div>
              </div>
            </div>

          

          {/* Image Upload Modal */}
          {showImageUpload && (
            <div className="image-upload-modal">
              <div className="modal-content">
                <h3>{this.props.t('profile-screen:modal.title')}</h3>
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={this._onImageSelect}
                  className="file-input"
                />
                {selectedImage && (
                  <div className="image-preview">
                    <img src={selectedImage} alt="Preview" />
                  </div>
                )}
                <div className="modal-actions">
                  <button 
                    onClick={this._onUploadPicture}
                    disabled={!selectedImage || updateProfilePicFetching}
                    className="upload-btn"
                  >
                    {updateProfilePicFetching ? 'Uploading...' : 'Upload'}
                  </button>
                  <button 
                    onClick={this._onCancelImageUpload}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>                  
                </div>
              </div>
            </div>
          )}

          {/* Personal Information Section */}
          <div className="personal-info-section">
            <h2 className="section-header">{this.props.t('profile-screen:section.title.personal-information')}</h2>
            <div className="info-container">
              <div className="info-item">
                <span className="info-label">{this.props.t('profile-screen:listitem.title.name')}</span>
                <span className="info-value">{name || 'Not set'}</span>
              </div>
              
              <div className="info-item">
                <span className="info-label">{this.props.t('profile-screen:listitem.title.date-of-birth')}</span>
                <span className="info-value">
                  {dob ? `${dob.getDate()}/${dob.getMonth() + 1}/${dob.getFullYear()}` : 'Not set'}
                </span>
              </div>
              
              <div className="info-item">
                <span className="info-label">{this.props.t('profile-screen:listitem.title.age')}</span>
                <span className="info-value">
                  {ageYear > 0 ? `${ageYear}${this.props.t('profile-screen:listitem.title.text.age-year')} ` : ''}
                  {ageMonth > 0 ? `${ageMonth}${this.props.t('profile-screen:listitem.title.text.age-month')} ` : ''}
                  {ageMonths > 0 ? `(${ageMonths} ${this.props.t('profile-screen:listitem.title.text.age-months')})` : ''}
                </span>
              </div>
              
              <div className="info-item">
                <span className="info-label">{this.props.t('profile-screen:listitem.title.programmes')}</span>
                <span className="info-value">{programmes || 'Not set'}</span>
              </div>
            </div>
          </div>

          {/* Withdrawal Section */}
          <div className="withdrawal-section">
            <div className="withdrawal-container">
              <div className="withdrawal-item">
                <button 
                  className="withdrawal-btn"
                  onClick={this._onWithdrawals}
                  disabled={this.state.withdrawalLoading}
                >
                  {this.state.withdrawalLoading
                    ? this.props.t('profile-screen:loading.processing') || 'Processing...'
                    : this.props.t('profile-screen:listitem.title.withdrawal')}
                  <ArrowRightIcon className="withdrawal-icon" />
                </button>
              </div>
            </div>
          </div>

          {/* Personal Information Message - Phase 4: Updated Text */}
          <div className="personal-info-message">
            <p>{this.props.t('profile-screen:section.message.personal-information')}</p>
          </div>

          {/* Downloads Section */}
          {this.renderDownload()}

          <AlertBox
            title={this.state.updateProfileType === 'SUCCESS' ? this.props.t('profile-screen:alert.upload-profile-pic-success.title') : this.props.t('profile-screen:alert.update-profile-pic-fail.title')}
            message={this.state.updateProfileType === 'SUCCESS' ? this.props.t('profile-screen:alert.upload-profile-pic-success.message') : this.props.t('profile-screen:alert.update-profile-pic-fail.message')}
            okAction={() => {this.setState({ updateProfilePopup: false, updateProfileType: '' });}}
            isVisible={this.state.updateProfilePopup}
            onClose={() => {}}
          />
          
        </div>
        </div>
      </div>
    );
  }
}

// Map Redux state to props
const mapStateToProps = (state: any) => ({
  userData: GetMeSelectors.getData(state),
  getMeFetching: GetMeSelectors.isFetching(state),
  getMeSuccess: GetMeSelectors.isSuccess(state),
  getMeError: GetMeSelectors.hasError(state),
  updateProfilePicData: UpdateProfilePicSelectors.getData(state),
  updateProfilePicFetching: UpdateProfilePicSelectors.isFetching(state),
  updateProfilePicSuccess: UpdateProfilePicSelectors.isSuccess(state),
  updateProfilePicError: UpdateProfilePicSelectors.hasError(state),
  children: GetChildrenSelectors.getChildren(state),
  getChildrenFetching: GetChildrenSelectors.isFetching(state),
  getChildrenSuccess: GetChildrenSelectors.isSuccess(state),
  getChildrenError: GetChildrenSelectors.hasError(state),
  currentSettings: state.currentSettings,
  createWithdrawalData: CreateWithdrawalsSelectors.getDataWithdrawals(state),
  createWithdrawalFetching: CreateWithdrawalsSelectors.isFetching(state),
  createWithdrawalSuccess: CreateWithdrawalsSelectors.isSuccess(state),
  createWithdrawalError: CreateWithdrawalsSelectors.hasError(state),
});

// Map Redux actions to props
const mapDispatchToProps = {
  getMe: GetMeActions.getMeRequest,
  updateProfilePic: UpdateProfilePicActions.updateProfilePicRequest,
  getChildren: GetChildrenActions.getChildrenRequest,
  createWithdrawals: CreateWithdrawalActions.createWithdrawalsRequest,
  clearWithdrawalData: CreateWithdrawalActions.clearWithdrawalData,
};

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(ProfilePage));
